import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Overline } from "../components/motion";
import { CONTACT, INTERESTS } from "../data";
import { Phone, Mail, MapPin, Loader2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, ...props }) => (
  <div className="relative">
    <label className="overline text-ink/40 block mb-3">{label}</label>
    <input
      {...props}
      className="w-full bg-transparent border-b border-ink/20 focus:border-brand-green py-3 font-body text-ink placeholder:text-ink/25 outline-none transition-colors duration-300"
    />
  </div>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", interest: INTERESTS[0], message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSent(true);
      toast.success("Enquiry received. Our team will be in touch shortly.");
      setForm({ name: "", email: "", company: "", phone: "", interest: INTERESTS[0], message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative pt-40 pb-24 md:pt-48 md:pb-32 bg-surface" data-testid="contact-section">
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal><Overline>06 — Let's Build</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-heading font-extrabold tracking-tighter text-ink text-4xl sm:text-5xl lg:text-6xl leading-[0.98]">
                Capital for the<br /><span className="font-display italic font-light text-ink/60">transition ahead.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 font-body text-ink/60 leading-relaxed max-w-md">
                Whether you are an operator seeking asset finance or an institution
                exploring co-lending and structured exposure — start the conversation.
              </p>
            </Reveal>

            <div className="mt-12 space-y-6">
              {[
                { icon: Phone, label: "Phone", value: CONTACT.phone, href: CONTACT.phoneHref, tid: "contact-phone" },
                { icon: Mail, label: "Partnerships", value: CONTACT.emailPrimary, href: `mailto:${CONTACT.emailPrimary}`, tid: "contact-email-primary" },
                { icon: Mail, label: "General enquiries", value: CONTACT.emailGeneral, href: `mailto:${CONTACT.emailGeneral}`, tid: "contact-email-general" },
                { icon: MapPin, label: "Registered office", value: CONTACT.address, tid: "contact-address" },
              ].map((c) => (
                <div key={c.label} className="flex items-start gap-4 group" data-testid={c.tid}>
                  <div className="w-10 h-10 border border-ink/15 flex items-center justify-center shrink-0 group-hover:border-brand-green transition-colors duration-300">
                    <c.icon size={16} className="text-ink/70 group-hover:text-brand-green transition-colors duration-300" />
                  </div>
                  <div>
                    <div className="overline text-ink/35 text-[9px]">{c.label}</div>
                    {c.href ? (
                      <a href={c.href} className="font-body text-ink/85 hover:text-brand-green transition-colors duration-300 text-sm leading-relaxed">{c.value}</a>
                    ) : (
                      <div className="font-body text-ink/85 text-sm leading-relaxed max-w-xs">{c.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="border border-ink/10 bg-bg p-8 md:p-12">
                {sent ? (
                  <div className="py-16 text-center" data-testid="enquiry-success">
                    <div className="w-16 h-16 mx-auto border border-brand-green flex items-center justify-center">
                      <ArrowRight className="text-brand-green" />
                    </div>
                    <h3 className="mt-8 font-heading font-bold text-ink text-2xl">Thank you.</h3>
                    <p className="mt-3 font-body text-ink/55 max-w-sm mx-auto">Your enquiry has reached our team. We'll respond to serious enquiries within two business days.</p>
                    <button onClick={() => setSent(false)} data-testid="enquiry-reset-btn" className="mt-8 overline text-brand-green hover:text-ink transition-colors">Send another →</button>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-8" data-testid="enquiry-form">
                    <div className="grid sm:grid-cols-2 gap-8">
                      <Field label="Full name *" value={form.name} onChange={set("name")} placeholder="Your name" data-testid="enquiry-name" />
                      <Field label="Email *" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="enquiry-email" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-8">
                      <Field label="Company / Organisation" value={form.company} onChange={set("company")} placeholder="Company" data-testid="enquiry-company" />
                      <Field label="Phone" value={form.phone} onChange={set("phone")} placeholder="Phone number" data-testid="enquiry-phone" />
                    </div>
                    <div>
                      <label className="overline text-ink/40 block mb-3">Area of interest</label>
                      <select
                        value={form.interest}
                        onChange={set("interest")}
                        data-testid="enquiry-interest"
                        className="w-full bg-transparent border-b border-ink/20 focus:border-brand-green py-3 font-body text-ink outline-none transition-colors duration-300 [&>option]:bg-surface2"
                      >
                        {INTERESTS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="overline text-ink/40 block mb-3">Message *</label>
                      <textarea
                        value={form.message}
                        onChange={set("message")}
                        rows={4}
                        data-testid="enquiry-message"
                        placeholder="Tell us about your requirement…"
                        className="w-full bg-transparent border-b border-ink/20 focus:border-brand-green py-3 font-body text-ink placeholder:text-ink/25 outline-none transition-colors duration-300 resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="enquiry-submit-btn"
                      className="inline-flex items-center gap-3 font-heading text-sm font-semibold tracking-wide bg-brand-green text-white px-9 py-4 hover:bg-ink hover:text-white transition-colors duration-300 disabled:opacity-60"
                    >
                      {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Submit Enquiry <ArrowRight size={16} /></>}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
